<template>
  <nav class="s-button-list">
    <ul class="s-button-list__list" :style="gridStyle">
      <li
        v-for="(item, index) in visibleItems"
        v-show="isExpanded || !maxItems || index < maxItems"
        :key="`${item.name}-${index}`"
        class="s-button-list__item"
      >
        <SButton
          :action-type="actionType"
          class="s-button-list__button"
          :color="buttonColor ?? 'primary'"
          :href="item.href"
          opaque
          :prefix-icon="item.prefixIcon ?? prefixIcon"
          :size="size"
          :suffix-icon="item.suffixIcon ?? suffixIcon"
          :tag
          :text-align="alignment"
          :to="item.to"
        >
          {{ item.name }}
        </SButton>
      </li>
    </ul>

    <div v-if="hasMoreItems" class="s-button-list__show-more">
      <SButton
        color="primary"
        size="sm"
        suffix-icon="far fa-chevron-down"
        @click="showAllItems"
      >
        {{ buttonLabel }}
      </SButton>
    </div>
  </nav>
</template>

<script setup lang="ts">
import type { Component } from "vue";

import { computed, ref } from "vue";

import type { ActionType, Size } from "@solvari/common-fe";
import type {
  GridMinWidth,
  GridMinWidthSize,
} from "@solvari/common-fe/composables";

import { SButton } from "@solvari/common-fe";
import { twSizeToPx } from "@solvari/common-fe/composables";
import { useI18nGeneral } from "@solvari/translations";

type SButtonListItem = {
  href?: string;
  name: string;
  prefixIcon?: string;
  suffixIcon?: string;
  to?: Record<string, unknown> | string;
};

const { tr } = useI18nGeneral();

const props = withDefaults(
  defineProps<{
    actionType?: ActionType;
    activeSlug?: string;
    alignment?: "center" | "left" | "right";
    buttonColor?:
      | "accent"
      | "danger"
      | "gray"
      | "primary"
      | "success"
      | "warning";
    items: SButtonListItem[];
    maxItems?: number;
    minWidth?: GridMinWidth;
    prefixIcon?: string;
    renderAll?: boolean;
    showMoreLabel?: string;
    size?: Size;
    suffixIcon?: string;
    tag?: Component | "a" | "button" | "label" | "RouterLink" | "span";
  }>(),
  { actionType: "tertiary-soft", alignment: "left", size: "sm" },
);

const gridStyle = computed(() => {
  if (!props.minWidth) {
    return undefined;
  }
  const minPx = twSizeToPx(Number(props.minWidth) as GridMinWidthSize);
  return {
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${minPx}px, 100%), 1fr))`,
  };
});

const isExpanded = ref(false);

const visibleItems = computed(() => {
  if (props.renderAll || !props.maxItems || isExpanded.value) {
    return props.items;
  }
  return props.items.slice(0, props.maxItems);
});

const hasMoreItems = computed(
  () =>
    props.maxItems && !isExpanded.value && props.items.length > props.maxItems,
);

const buttonLabel = computed(
  () => props.showMoreLabel ?? tr("general.show_more"),
);

function showAllItems() {
  isExpanded.value = true;
}
</script>

<style lang="postcss">
.s-button-list {
  @apply flex flex-col;

  &__list {
    @apply grid w-full grid-cols-2 gap-2;

    @screen 800 {
      @apply grid-cols-auto-64 grid-cols-auto-grow;
    }
  }

  &__item {
    @apply list-none;
  }

  &__button {
    @apply h-full w-full;
  }

  &__show-more {
    @apply mt-4 flex justify-center;
  }
}
</style>
